import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getWarehouse,
  postWarehouse,
  getEnabledWarehouse,
} from "@/services/apis/warehouseApi";

// Deliberately NOT gated on `roleAccess.APP_PERMISSION`: that made the warehouse
// call wait for the whole RBAC chain (roles → per-role permissions) even though it
// only needs a session. It now fires in parallel with RBAC; ProtectedRoute still
// consults roleAccess separately before showing the WarehouseScreen.
export const useWarehouse = (userDetails: any, currentUser: any) => {
  const warehouseQuery = useQuery({
    queryKey: ["warehouse", userDetails?.userId],
    queryFn: () => getWarehouse(userDetails?.userId),
    enabled: !!userDetails?.userId && !!currentUser,
    staleTime: Infinity, // Data will never become stale
    gcTime: Infinity, // Cache will never be garbage collected
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false,
    retryOnMount: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    retry: false,
  });

  const enabledWarehouseQuery = useQuery({
    queryKey: ["enabledWarehouse", userDetails?.userId],
    queryFn: () => getEnabledWarehouse(userDetails?.userId),
    enabled: false, // Don't fetch automatically
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false, // Don't refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  const queryClient = useQueryClient();

  const createWarehouseMutation = useMutation({
    mutationFn: postWarehouse,
    onSuccess: (newWarehouse) => {
      // Update the warehouse query data by adding the new warehouse to the array
      queryClient.setQueryData(
        ["warehouse", userDetails?.userId],
        (oldData: any) => {
          const currentWarehouses = Array.isArray(oldData) ? oldData : [];
          return [...currentWarehouses, newWarehouse];
        }
      );
    },
  });

  return {
    ...warehouseQuery,
    refetchWarehouse: warehouseQuery.refetch,
    enabledWarehouses: enabledWarehouseQuery.data,
    isEnabledWarehousesLoading: enabledWarehouseQuery.isLoading,
    enabledWarehousesError: enabledWarehouseQuery.error,
    fetchEnabledWarehouses: enabledWarehouseQuery.refetch,
    createWarehouse: createWarehouseMutation.mutateAsync,
    isCreatingWarehouse: createWarehouseMutation.isPending,
    createWarehouseError: createWarehouseMutation.error,
    createWarehouseSuccess: createWarehouseMutation.isSuccess,
  };
};
